/**
 * EditJobModal — the dialog opened from a Jobs row's Actions menu (Claude
 * Design "Edit Job modal" handoff).
 *
 * Fixed header + footer, two scrolling columns: vehicle and services on the
 * left, status and crew on the right. Money is computed live from scope +
 * extras; the backend recomputes authoritatively on save.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { api } from '../api';
import {
  ALL_DEPARTMENTS, JOB_DESCRIPTIONS, FRONTEND_PRICING, DEPT_AUTO,
  SCOPE_OPTS, HOUR_OPTS, EDIT_EXTRAS, COLOURS, calcFrontendRevenue,
} from './jobPricing';

/* ---- tokens ----------------------------------------------------------- */
const TEAL = '#4A7A91', TEAL_TINT = '#e8f2f7', NAVY = '#1e3a4a', INK = '#1a1a2e';
const RED = '#c53030', GREEN = '#15803d', AMBER = '#b54708';
const G1 = '#475467', G2 = '#667085', G3 = '#98a2b3', G4 = '#c3ccd3';
const LINE = '#dfe3e7', LINE2 = '#eef0f2', PANEL = '#fbfcfd';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const NUMS = { fontVariantNumeric: 'tabular-nums' };
const MARGIN_TARGET = 25;   // % — below this the footer warns

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;
const signedMoney = (v) => (v < 0 ? `−$${Math.abs(v).toFixed(2)}` : `$${v.toFixed(2)}`);

const inp = {
  height: 40, padding: '0 10px', width: '100%', boxSizing: 'border-box',
  border: `1.5px solid ${LINE}`, borderRadius: 4, fontSize: 14, fontWeight: 600,
  color: INK, background: '#fff', outline: 'none', fontFamily: 'inherit',
};
const sectionLbl = { fontSize: 9, fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase', color: '#8a949c', marginBottom: 8 };
const fieldLbl = { fontSize: 10.5, fontWeight: 600, color: G2, marginBottom: 4, display: 'block' };

const STATUSES = [
  { v: 'pending',   l: 'Pending',   bg: '#fef3c7', fg: '#a16207' },
  { v: 'approved',  l: 'Approved',  bg: '#dcfce7', fg: '#15803d' },
  { v: 'rejected',  l: 'Rejected',  bg: '#fee2e2', fg: '#b91c1c' },
  { v: 'completed', l: 'Completed', bg: '#dcfce7', fg: '#15803d' },
];

// Engagement tag per contractor type.
const ENGAGEMENT = {
  hourly:         { l: 'Hourly',       bg: '#ede9fe', fg: '#6d28d9' },
  commissioned:   { l: 'Commissioned', bg: '#e8f2f7', fg: '#1e3a4a' },
  office_cleaner: { l: 'Office',       bg: '#d1fadf', fg: '#039855' },
  admin:          { l: 'Director',     bg: '#fef3c7', fg: '#a16207' },
};
const initials = (n) => String(n || '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const firstNameOf = (n) => String(n || '').trim().split(/\s+/)[0];

function parseExtrasForEdit(raw) {
  return (raw || []).map(e => {
    if (typeof e === 'string') return { service: e, units: 1, department: '' };
    return { service: e.service || e, units: parseFloat(e.units) || 1, department: e.department || '' };
  });
}

function DeptSelect({ value, onChange, small }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      style={{ ...inp, height: small ? 26 : 40, fontSize: small ? 10.5 : 13, width: 'auto', cursor: 'pointer' }}>
      <option value="">— Dept —</option>
      {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
    </select>
  );
}

// 30/50/70/Full (or hours) segmented control.
function ScopeSeg({ options, value, onChange, label, height = 30 }) {
  return (
    <div style={{ display: 'inline-flex', border: `1.5px solid ${LINE}`, borderRadius: 4, overflow: 'hidden' }}>
      {options.map(o => {
        const on = value === o.v;
        return (
          <button key={o.v} type="button" onClick={() => onChange(o.v)} style={{
            height, padding: '0 10px', border: 'none', cursor: 'pointer',
            fontSize: height > 26 ? 12 : 10, fontWeight: 700,
            background: on ? '#1a1a1a' : '#fff', color: on ? '#FFD000' : G1,
          }}>{label ? label(o.v) : o.l}</button>
        );
      })}
    </div>
  );
}

export default function EditJobModal({ job, contractors, onSave, onClose, onDelete }) {
  const initialEmpIds = useMemo(() => {
    const raw = job.employee_ids;
    if (Array.isArray(raw)) return raw.map(Number).filter(n => !isNaN(n));
    if (typeof raw === 'string' && raw.length > 0) {
      return raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    }
    return [];
  }, [job.employee_ids]);

  const initialComment = useMemo(() => {
    try {
      const c = JSON.parse(job.comments || '[]');
      return Array.isArray(c) ? c.join(', ') : String(job.comments || '');
    } catch { return String(job.comments || ''); }
  }, [job.comments]);

  const [date,         setDate]         = useState(job.date || '');
  const [stockNumber,  setStockNumber]  = useState(job.stock_number || '');
  const [model,        setModel]        = useState(job.model || '');
  const [colour,       setColour]       = useState(job.colour || '');
  const [jobDesc,      setJobDesc]      = useState(job.job_description);
  const [mainUnits,    setMainUnits]    = useState(job.units ?? 1);
  const [mainDept,     setMainDept]     = useState(job.department || '');
  const [extras,       setExtras]       = useState(parseExtrasForEdit(job.extra_jobs));
  const [comment,      setComment]      = useState(initialComment);
  const [status,       setStatus]       = useState(job.status);
  const [empIds,       setEmpIds]       = useState(initialEmpIds);
  const [showInactive, setShowInactive] = useState(false);
  const [saving,       setSaving]       = useState(false);

  const preview = calcFrontendRevenue(jobDesc, extras, mainUnits);
  const revenue = preview.full;             // ex GST
  const payout  = preview.pay;
  const gst     = revenue * 0.1;
  const margin  = revenue - payout;
  const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
  const payoutPct = revenue > 0 ? Math.round((payout / revenue) * 100) : 0;
  const payEach = payout / (empIds.length || 1);

  // Dirty check — drives the "Unsaved changes" pill.
  const dirty =
    date !== (job.date || '') || stockNumber !== (job.stock_number || '') ||
    model !== (job.model || '') || colour !== (job.colour || '') ||
    jobDesc !== job.job_description || String(mainUnits) !== String(job.units ?? 1) ||
    mainDept !== (job.department || '') || comment !== initialComment ||
    status !== job.status ||
    JSON.stringify(extras) !== JSON.stringify(parseExtrasForEdit(job.extra_jobs)) ||
    JSON.stringify([...empIds].sort()) !== JSON.stringify([...initialEmpIds].sort());

  const displayContractors = contractors.filter(c => {
    if (c.type === 'admin' && !empIds.includes(c.id)) return false;
    return showInactive ? true : c.is_active !== 0;
  });

  const toggleEmp = (id) => setEmpIds(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  const isExtraOn = (svc) => extras.some(e => e.service === svc);
  const toggleExtra = (svc) => setExtras(prev => {
    if (prev.find(e => e.service === svc)) return prev.filter(e => e.service !== svc);
    return [...prev, { service: svc, units: 1, department: svc === 'BUFFING' ? mainDept : '' }];
  });
  const setExtraUnits = (svc, u) => setExtras(prev => prev.map(e => e.service === svc ? { ...e, units: u } : e));
  const setExtraDept  = (svc, d) => setExtras(prev => prev.map(e => e.service === svc ? { ...e, department: d } : e));
  const handleDescChange = (val) => {
    setJobDesc(val);
    const auto = DEPT_AUTO[val];
    if (auto) setMainDept(auto);
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.updateJob(job.id, {
        date, stock_number: stockNumber, model, colour: colour || null,
        job_description: jobDesc, extra_jobs: extras, status,
        employee_ids: empIds, department: mainDept || null, units: mainUnits,
        comments: comment.trim() ? comment.split(/\s*·\s*|\s*,\s*/).filter(Boolean) : [],
      });
      onSave();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }, [job.id, date, stockNumber, model, colour, jobDesc, extras, status, empIds, mainDept, mainUnits, comment, onSave]);

  // Esc closes, Cmd/Ctrl+Enter saves — both advertised in the footer, so both
  // are actually bound.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSave(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, handleSave]);

  const submitted = job.created_at
    ? new Date(job.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' })
    : null;

  const marginColor = marginPct >= MARGIN_TARGET ? GREEN : marginPct >= 0 ? AMBER : RED;

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,10,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
      <div style={{
        width: '100%', maxWidth: 920, maxHeight: 660, background: '#fff', borderRadius: 10,
        boxShadow: '0 24px 64px rgba(16,24,40,.28)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>

        {/* ── Header (fixed) ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 22px', borderBottom: `1px solid ${LINE2}`, flex: 'none' }}>
          <div>
            <div style={sectionLbl}>Edit job</div>
            <div style={{ fontFamily: MONO, fontSize: 19, fontWeight: 800, color: INK, ...NUMS }}>#{job.id}</div>
            {(job.submitted_by_name || submitted) && (
              <div style={{ fontSize: 11.5, fontWeight: 600, color: G3, marginTop: 2 }}>
                submitted{job.submitted_by_name ? ` by ${firstNameOf(job.submitted_by_name)}` : ''}{submitted ? ` · ${submitted}` : ''}
              </div>
            )}
          </div>
          <span style={{ width: 1, height: 34, background: LINE2 }} />
          {mainDept && <span style={{ background: '#f4f6f8', color: INK, fontWeight: 700, fontSize: 11, padding: '3px 9px', borderRadius: 6 }}>{mainDept}</span>}
          {(() => { const s = STATUSES.find(x => x.v === status); return s ? (
            <span style={{ background: s.bg, color: s.fg, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 6 }}>{s.l}</span>
          ) : null; })()}
          <span style={{ flex: 1 }} />
          {dirty && (
            <span style={{ background: '#fef0c7', color: AMBER, fontWeight: 700, fontSize: 11, padding: '4px 10px', borderRadius: 20 }}>● Unsaved changes</span>
          )}
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid ${LINE2}`, background: '#fff', color: G2, fontSize: 16, cursor: 'pointer', flex: 'none' }}>✕</button>
        </div>

        {/* ── Body (scrolls) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr .85fr', overflowY: 'auto', flex: 1 }}>

          {/* Left: vehicle + services */}
          <div style={{ padding: '16px 20px' }}>
            <div style={sectionLbl}>Vehicle</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={fieldLbl}>Stock number</label>
                <input value={stockNumber} onChange={e => setStockNumber(e.target.value.toUpperCase())}
                  style={{ ...inp, fontFamily: MONO, fontWeight: 800, letterSpacing: 1 }} />
              </div>
              <div>
                <label style={fieldLbl}>Model</label>
                <input value={model} onChange={e => setModel(e.target.value)} style={inp} />
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={fieldLbl}>Date</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, flex: 1 }} />
                <button type="button" onClick={() => setDate(new Date().toISOString().slice(0, 10))}
                  style={{ ...inp, width: 'auto', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: TEAL }}>Today</button>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={fieldLbl}>Colour {colour && <span style={{ fontWeight: 700, color: NAVY }}>· {colour}</span>}</label>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {COLOURS.map(c => {
                  const on = colour === c.name;
                  return (
                    <div key={c.name} onClick={() => setColour(on ? '' : c.name)} style={{ cursor: 'pointer', textAlign: 'center', width: 42 }}>
                      <div style={{
                        height: 26, borderRadius: 4, background: c.sw, border: c.bd || '1px solid transparent',
                        boxShadow: on ? `0 0 0 2px ${TEAL}, 0 0 0 4px rgba(74,122,145,.18)` : 'none',
                      }} />
                      <div style={{ fontSize: 8.5, marginTop: 3, fontWeight: on ? 700 : 500, color: on ? NAVY : G2 }}>{c.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ ...sectionLbl, marginTop: 16 }}>Main service</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={jobDesc} onChange={e => handleDescChange(e.target.value)}
                style={{ ...inp, flex: 1, minWidth: 170, fontWeight: 800, cursor: 'pointer' }}>
                {JOB_DESCRIPTIONS.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
              <DeptSelect value={mainDept} onChange={setMainDept} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
              <ScopeSeg options={SCOPE_OPTS} value={mainUnits} onChange={setMainUnits} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: G2, ...NUMS }}>
                {money((FRONTEND_PRICING[jobDesc]?.full || 0) * mainUnits)} charged ·{' '}
                {money((FRONTEND_PRICING[jobDesc]?.pay || 0) * mainUnits)} pay
              </span>
            </div>

            <div style={{ ...sectionLbl, marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
              <span>Extra services</span>
              {extras.length > 0 && (
                <span style={{ color: TEAL, ...NUMS }}>{extras.length} selected · +{money(revenue - (FRONTEND_PRICING[jobDesc]?.full || 0) * mainUnits)}</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              {EDIT_EXTRAS.map(svc => {
                const on = isExtraOn(svc);
                const ep = FRONTEND_PRICING[svc] || { full: 0 };
                return (
                  <button key={svc} type="button" onClick={() => toggleExtra(svc)} style={{
                    height: 38, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px',
                    border: `1.5px solid ${on ? TEAL : LINE}`, borderRadius: 4, cursor: 'pointer',
                    background: on ? TEAL_TINT : '#fff', color: on ? NAVY : G1,
                    fontSize: 12, fontWeight: 700, textAlign: 'left',
                  }}>
                    <span style={{ flex: 1 }}>{svc}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: on ? TEAL : G4, ...NUMS }}>${ep.full}</span>
                  </button>
                );
              })}
            </div>

            {/* Per-extra scope — each extra is scoped independently */}
            {extras.length > 0 && (
              <div style={{ border: `1px solid ${LINE2}`, borderRadius: 4, marginTop: 10 }}>
                {extras.map((ex, i) => {
                  const isHours = ex.service === 'HOURS';
                  const opts = isHours ? HOUR_OPTS.map(h => ({ v: h, l: `${h}h` })) : SCOPE_OPTS;
                  const ep = FRONTEND_PRICING[ex.service] || { full: 0, pay: 0 };
                  return (
                    <div key={ex.service} style={{
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '7px 10px', borderTop: i ? `1px solid ${LINE2}` : 'none',
                    }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: NAVY, minWidth: 92 }}>{ex.service}</span>
                      <div style={{ maxWidth: 260, overflowX: 'auto' }}>
                        <ScopeSeg options={opts} value={ex.units} onChange={u => setExtraUnits(ex.service, u)} height={24} />
                      </div>
                      <DeptSelect small value={ex.department} onChange={d => setExtraDept(ex.service, d)} />
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, fontWeight: 700, ...NUMS }}>{money(ep.full * ex.units)}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: RED, ...NUMS }}>{money(ep.pay * ex.units)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <label style={fieldLbl}>Comments</label>
              <input value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Delivery · Demo · customer car…" style={inp} />
            </div>
          </div>

          {/* Right: status + crew */}
          <div style={{ padding: '16px 20px', borderLeft: `1px solid ${LINE2}`, background: PANEL }}>
            <div style={sectionLbl}>Status</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              {STATUSES.map(s => {
                const on = status === s.v;
                return (
                  <button key={s.v} type="button" onClick={() => setStatus(s.v)} style={{
                    height: 36, borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    border: `1.5px solid ${on ? s.fg : LINE}`,
                    background: on ? s.bg : '#fff', color: on ? s.fg : G1,
                  }}>{s.l}</button>
                );
              })}
            </div>

            <div style={{ ...sectionLbl, marginTop: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Assigned crew</span>
              <span style={{ background: TEAL_TINT, color: NAVY, borderRadius: 20, padding: '1px 8px', fontSize: 10, ...NUMS }}>{empIds.length}</span>
              <span style={{ flex: 1 }} />
              <span onClick={() => setShowInactive(v => !v)} style={{ cursor: 'pointer', color: showInactive ? TEAL : G3, fontSize: 9 }}>Show inactive</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {displayContractors.map(emp => {
                const on = empIds.includes(emp.id);
                const eng = ENGAGEMENT[emp.type] || { l: emp.type || '—', bg: '#f2f4f6', fg: G3 };
                const inactive = emp.is_active === 0;
                return (
                  <div key={emp.id} onClick={() => toggleEmp(emp.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', borderRadius: 4,
                    border: `1.5px solid ${on ? TEAL : 'transparent'}`, background: on ? TEAL_TINT : 'transparent',
                    cursor: 'pointer', opacity: inactive ? 0.55 : 1,
                  }}>
                    <input type="checkbox" checked={on} readOnly style={{ width: 17, height: 17, accentColor: TEAL, cursor: 'pointer', flex: 'none' }} />
                    <span style={{
                      width: 26, height: 26, borderRadius: '50%', background: eng.bg, color: eng.fg,
                      fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
                    }}>{initials(emp.name)}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {firstNameOf(emp.name)}
                    </span>
                    <span style={{ background: inactive ? '#f2f4f6' : eng.bg, color: inactive ? G3 : eng.fg, fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 6 }}>
                      {inactive ? 'Inactive' : eng.l}
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 14, background: '#fff', border: `1px solid ${LINE2}`, borderRadius: 4, padding: '9px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: G2 }}>Pay each</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: INK, ...NUMS }}>
                {money(payEach)}{empIds.length > 1 ? ` × ${empIds.length}` : ''}
              </span>
            </div>
          </div>
        </div>

        {/* ── Footer (fixed) ── */}
        <div style={{ borderTop: `1px solid ${LINE2}`, flex: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 22px', gap: 0, flexWrap: 'wrap' }}>
            <div style={{ paddingRight: 18 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#8a949c' }}>Charged</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: INK, ...NUMS }}>{money(revenue * 1.1)}</div>
              <div style={{ fontSize: 10, color: G3, ...NUMS }}>ex {money(revenue)} · GST {money(gst)}</div>
            </div>
            <span style={{ width: 1, height: 38, background: LINE2 }} />
            <div style={{ padding: '0 18px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#8a949c' }}>Staff payout</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: RED, ...NUMS }}>{money(payout)}</div>
              <div style={{ fontSize: 10, color: G3, ...NUMS }}>{payoutPct}% of charged</div>
            </div>
            <span style={{ width: 1, height: 38, background: LINE2 }} />
            <div style={{ padding: '0 18px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#8a949c' }}>Margin</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: marginColor, ...NUMS }}>{signedMoney(margin)}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: marginColor, ...NUMS }}>{marginPct.toFixed(1)}%</span>
              </div>
            </div>
            {marginPct < MARGIN_TARGET && (
              <span style={{
                marginLeft: 12, fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6,
                background: margin < 0 ? '#fee2e2' : '#fef0c7', color: margin < 0 ? RED : AMBER,
              }}>
                {margin < 0 ? 'Payout exceeds what the job charges' : `Margin below the ${MARGIN_TARGET}% target`}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 22px', borderTop: `1px solid ${LINE2}`, flexWrap: 'wrap' }}>
            {onDelete && (
              <button onClick={() => { if (window.confirm(`Delete job #${job.id}? This cannot be undone.`)) onDelete(job.id); }}
                style={{ height: 36, padding: '0 14px', border: '1.5px solid #f3c6c6', borderRadius: 4, background: '#fff', color: RED, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                Delete job
              </button>
            )}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 10.5, fontWeight: 600, color: G3 }}>Esc to cancel · Ctrl↵ to save</span>
            <button onClick={onClose} style={{ height: 36, padding: '0 16px', border: `1.5px solid ${LINE}`, borderRadius: 4, background: '#fff', color: G1, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ height: 36, padding: '0 18px', border: 'none', borderRadius: 4, background: TEAL, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
